import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiClient } from '../../lib/apiClient';
import styles from './DataGovernanceDashboard.module.css';

interface ConflictRecord {
  id: string;
  table_name: string;
  record_id: string;
  version_number: number;
  changed_by: string;
  changed_at: string;
  change_summary: string;
}

interface PendingApproval {
  id: string;
  table_name: string;
  record_id: string;
  change_type: string;
  proposed_by: string;
  status: string;
  created_at: string;
  reason: string;
}

interface ActiveSession {
  id: string;
  table_name: string;
  record_id: string;
  display_name: string;
  locked_at: string;
  last_activity_at: string;
}

export default function DataGovernanceDashboard() {
  const { organization } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'conflicts' | 'approvals' | 'sessions'>('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalVersions: 0,
    pendingApprovals: 0,
    activeSessions: 0,
    recentChanges: 0,
  });
  const [recentVersions, setRecentVersions] = useState<ConflictRecord[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [activeSessions, setActiveSessions] = useState<ActiveSession[]>([]);

  useEffect(() => {
    if (organization) {
      loadData();
    }
  }, [organization]);

  const loadData = async () => {
    if (!organization) return;

    setLoading(true);
    try {
      await Promise.all([loadStats(), loadRecentVersions(), loadPendingApprovals(), loadActiveSessions()]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!organization) return;
    const data = await apiClient.get<typeof stats>(`/orgs/${organization.id}/governance/stats`);
    if (data) setStats(data);
  };

  const loadRecentVersions = async () => {
    if (!organization) return;
    const data = await apiClient.get<ConflictRecord[]>(`/orgs/${organization.id}/governance/version-history?limit=10`);
    setRecentVersions(data || []);
  };

  const loadPendingApprovals = async () => {
    if (!organization) return;
    const data = await apiClient.get<PendingApproval[]>(`/orgs/${organization.id}/governance/pending-approvals`);
    setPendingApprovals(data || []);
  };

  const loadActiveSessions = async () => {
    if (!organization) return;
    const data = await apiClient.get<ActiveSession[]>(`/orgs/${organization.id}/governance/active-editors`);
    setActiveSessions(data || []);
  };

  const handleApproveApproval = async (approvalId: string) => {
    try {
      await apiClient.patch(`/governance/approvals/${approvalId}`, {
        status: 'approved',
        reviewed_at: new Date().toISOString(),
      });
      await loadData();
    } catch (err) {
      console.error('Failed to approve:', err);
    }
  };

  const handleRejectApproval = async (approvalId: string) => {
    try {
      await apiClient.patch(`/governance/approvals/${approvalId}`, {
        status: 'rejected',
        reviewed_at: new Date().toISOString(),
      });
      await loadData();
    } catch (err) {
      console.error('Failed to reject:', err);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading governance dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Data Governance Dashboard</h1>
        <p className={styles.subtitle}>Monitor changes, conflicts, and access control across your organization</p>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statTitle}>Total Changes</span>
            <svg className={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className={styles.statValue}>{stats.totalVersions}</h3>
          <p className={styles.statTrend}>All time</p>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statTitle}>Recent Changes</span>
            <svg className={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <h3 className={styles.statValue}>{stats.recentChanges}</h3>
          <p className={styles.statTrend}>Last 24 hours</p>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statTitle}>Pending Approvals</span>
            <svg className={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className={styles.statValue}>{stats.pendingApprovals}</h3>
          <p className={stats.pendingApprovals > 0 ? styles.statTrendNegative : styles.statTrend}>
            {stats.pendingApprovals > 0 ? 'Requires attention' : 'All clear'}
          </p>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statHeader}>
            <span className={styles.statTitle}>Active Sessions</span>
            <svg className={styles.statIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          </div>
          <h3 className={styles.statValue}>{stats.activeSessions}</h3>
          <p className={styles.statTrend}>Currently editing</p>
        </div>
      </div>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'conflicts' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('conflicts')}
        >
          Recent Changes
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'approvals' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('approvals')}
        >
          Pending Approvals ({stats.pendingApprovals})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'sessions' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          Active Sessions ({stats.activeSessions})
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Recent Activity</h2>
          {recentVersions.length === 0 ? (
            <div className={styles.emptyState}>
              <svg className={styles.emptyIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className={styles.emptyText}>No recent activity</p>
              <p className={styles.emptySubtext}>Changes will appear here once users start editing data</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Version</th>
                  <th>Change Summary</th>
                  <th>Changed At</th>
                </tr>
              </thead>
              <tbody>
                {recentVersions.map((version) => (
                  <tr key={version.id}>
                    <td>
                      <span className={`${styles.badge} ${styles.badgeInfo}`}>
                        {version.table_name.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>v{version.version_number}</td>
                    <td>{version.change_summary}</td>
                    <td>{new Date(version.changed_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'approvals' && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Pending Approvals</h2>
          {pendingApprovals.length === 0 ? (
            <div className={styles.emptyState}>
              <svg className={styles.emptyIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className={styles.emptyText}>No pending approvals</p>
              <p className={styles.emptySubtext}>High-risk changes requiring approval will appear here</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Change Type</th>
                  <th>Reason</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingApprovals.map((approval) => (
                  <tr key={approval.id}>
                    <td>
                      <span className={`${styles.badge} ${styles.badgeInfo}`}>
                        {approval.table_name.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <span className={`${styles.badge} ${styles.badgeWarning}`}>{approval.change_type}</span>
                    </td>
                    <td>{approval.reason || 'No reason provided'}</td>
                    <td>{new Date(approval.created_at).toLocaleString()}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                          onClick={() => handleApproveApproval(approval.id)}
                        >
                          Approve
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                          onClick={() => handleRejectApproval(approval.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Active Editing Sessions</h2>
          {activeSessions.length === 0 ? (
            <div className={styles.emptyState}>
              <svg className={styles.emptyIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
              <p className={styles.emptyText}>No active editing sessions</p>
              <p className={styles.emptySubtext}>Users currently editing records will appear here</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Table</th>
                  <th>Started</th>
                  <th>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {activeSessions.map((session) => (
                  <tr key={session.id}>
                    <td>
                      <span className={`${styles.badge} ${styles.badgeSuccess}`}>{session.display_name}</span>
                    </td>
                    <td>{session.table_name.replace(/_/g, ' ')}</td>
                    <td>{new Date(session.locked_at).toLocaleString()}</td>
                    <td>{new Date(session.last_activity_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
